/**
 * LanguageRow Component
 * Individual row for each language with flag preview, active toggle, and edit action
 */

import React from 'react';
import { TableRow, TableCell, Avatar, Switch, IconButton, Typography, Chip } from '@mui/material';
import { Pencil } from 'lucide-react';
import { Language } from '../../types/admin';

// ============================================
// Props Interface
// ============================================

interface LanguageRowProps {
  language: Language;
  onToggleActive: (key: string, active: boolean) => void;
  onEdit: (language: Language) => void;
  disabled?: boolean;
}

// ============================================
// LanguageRow Component
// ============================================

export const LanguageRow: React.FC<LanguageRowProps> = ({
  language,
  onToggleActive,
  onEdit,
  disabled = false,
}) => {
  const flagSrc = language.flagUrl;

  return (
    <TableRow
      hover
      sx={{
        '&:hover': {
          backgroundColor: 'rgba(59, 130, 246, 0.04)',
        },
      }}
    >
      {/* Flag Preview */}
      <TableCell>
        <Avatar
          src={flagSrc}
          alt={`${language.name} flag`}
          variant="rounded"
          sx={{
            width: 48,
            height: 32,
            backgroundColor: flagSrc ? 'transparent' : '#f1f5f9',
            color: '#64748b',
            fontSize: '0.75rem',
          }}
        >
          {!flagSrc && language.key.toUpperCase()}
        </Avatar>
      </TableCell>

      {/* Language Name */}
      <TableCell>
        <Typography variant="body1" className="font-medium text-gray-900 dark:text-slate-100">
          {language.name}
        </Typography>
      </TableCell>

      {/* Language Keys */}
      <TableCell>
        <div className="flex flex-wrap gap-1">
          <Chip
            label={language.key}
            size="small"
            sx={{
              backgroundColor: '#e0e7ff',
              color: '#4338ca',
              fontFamily: 'monospace',
              fontWeight: 600,
            }}
          />
          {(language.aliases ?? []).map((alias) => (
            <Chip
              key={alias}
              label={alias}
              size="small"
              variant="outlined"
              sx={{
                borderColor: '#cbd5e1',
                color: '#475569',
                fontFamily: 'monospace',
                fontWeight: 600,
              }}
            />
          ))}
        </div>
      </TableCell>

      {/* Active Status Switch */}
      <TableCell>
        <Switch
          checked={language.active}
          onChange={(e) => onToggleActive(language.key, e.target.checked)}
          disabled={disabled}
          sx={{
            '& .MuiSwitch-switchBase.Mui-checked': {
              color: '#3B82F6',
            },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
              backgroundColor: '#3B82F6',
            },
          }}
        />
      </TableCell>

      {/* Edit Button */}
      <TableCell align="right">
        <IconButton
          onClick={() => onEdit(language)}
          size="small"
          disabled={disabled}
          sx={{
            color: '#3B82F6',
            '&:hover': {
              backgroundColor: 'rgba(59, 130, 246, 0.08)',
            },
          }}
          title="Edit language"
        >
          <Pencil size={18} />
        </IconButton>
      </TableCell>
    </TableRow>
  );
};
